// Tracking utility for UTM and tracking parameters
import { setStorage, getStorage } from "./storage";

class TrackingManager {
  constructor() {
    this.trackingKeys = [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_term",
      "utm_content",
      "pubid",
      "ref_id",
      "sub4",
      "sub18",
      "sub19",
      "gclid",
      "fbclid",
      "msclkid",
      "clickid",
      "rtkcid",
    ];
  }

  // Get all URL parameters
  getUrlParams() {
    const params = new URLSearchParams(window.location.search);
    const result = {};

    for (const [key, value] of params.entries()) {
      result[key] = value;
    }

    return result;
  }

  // Test localStorage availability
  testLocalStorage() {
    try {
      const testKey = "__utm_test__";
      localStorage.setItem(testKey, "test");
      localStorage.removeItem(testKey);
      return true;
    } catch (error) {
      return false;
    }
  }

  // Save tracking parameters
  saveTrackingParams(params) {
    if (!this.testLocalStorage()) {
      return false;
    }

    const trackingParams = {};
    let hasTrackingParams = false;

    // Extract only tracking parameters
    this.trackingKeys.forEach((key) => {
      if (params[key]) {
        trackingParams[key] = params[key];
        hasTrackingParams = true;
      }
    });

    if (hasTrackingParams) {
      try {
        // Save to our storage system
        setStorage("trackingParams", trackingParams);
        setStorage("utm_data", trackingParams); // Also save to utm_data for API compatibility
        setStorage("trackingData", {
          params: trackingParams,
          timestamp: new Date().toISOString(),
          page: window.location.pathname,
          referrer: document.referrer,
        });

        return true;
      } catch (error) {
        return false;
      }
    }

    return false;
  }

  // Get saved tracking parameters
  getSavedTrackingParams() {
    try {
      const saved = getStorage("trackingParams");
      if (saved) {
        return saved;
      }
      return {};
    } catch (error) {
      return {};
    }
  }

  // Update buttons and links with tracking parameters
  updateButtons(params) {
    const buttons = document.querySelectorAll("a, button");

    if (!buttons.length) {
      return;
    }

    // Merge current params with saved params (current params take priority)
    const savedParams = this.getSavedTrackingParams();
    const allParams = { ...savedParams, ...params };

    const paramString = Object.keys(allParams)
      .map((key) => `${key}=${encodeURIComponent(allParams[key])}`)
      .join("&");

    if (paramString) {
      buttons.forEach((button) => {
        if (button.tagName === "A" && button.href) {
          try {
            const url = new URL(button.href);
            // Only add params to internal links or if href is relative
            if (
              url.hostname === window.location.hostname ||
              button.href.startsWith("/") ||
              button.href.startsWith("./")
            ) {
              url.search = paramString;
              button.href = url.toString();
            }
          } catch (error) {
            // Skip invalid URLs
          }
        }
      });
    }
  }

  // Clear tracking data
  clearTrackingData() {
    try {
      // Use our storage utility to clear tracking data
      localStorage.removeItem("trackingParams");
      localStorage.removeItem("utm_data");
      localStorage.removeItem("trackingData");
      sessionStorage.removeItem("trackingParams");
      sessionStorage.removeItem("utm_data");
      return true;
    } catch (error) {
      return false;
    }
  }

  // Initialize tracking
  init() {
    try {
      // Get URL parameters
      const params = this.getUrlParams();

      // Save tracking parameters
      const saveResult = this.saveTrackingParams(params);

      // Update buttons with parameters
      this.updateButtons(params);

      // Set up observer for dynamic content
      const observer = new MutationObserver(() => {
        this.updateButtons(params);
      });

      observer.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return true;
    } catch (error) {
      return false;
    }
  }

  // Get tracking parameters for API calls
  getTrackingParamsForAPI() {
    const saved = this.getSavedTrackingParams();
    const current = this.getUrlParams();
    const result = { ...saved, ...current };
    return result;
  }
}

// Create singleton instance
const trackingManager = new TrackingManager();

// Export functions
export const initTracking = () => trackingManager.init();
export const getTrackingParams = () =>
  trackingManager.getTrackingParamsForAPI();
export const clearTracking = () => trackingManager.clearTrackingData();
export const updateTrackingButtons = (params) =>
  trackingManager.updateButtons(params);

// Make available globally for debugging
if (typeof window !== "undefined") {
  window.trackingManager = trackingManager;
  window.initTracking = initTracking;
  window.getTrackingParams = getTrackingParams;
  window.clearTracking = clearTracking;
}
